import React from 'react';
import { Link } from 'react-router-dom';

const EnhancedFishCard = ({ fish }) => {
  const careLevelColors = {
    beginner: {
      bg: 'hsl(142, 76%, 36%, 0.1)',
      text: 'hsl(142, 76%, 25%)',
      border: 'hsl(142, 76%, 36%, 0.2)'
    },
    intermediate: {
      bg: 'hsl(45, 93%, 47%, 0.1)', 
      text: 'hsl(45, 93%, 35%)',
      border: 'hsl(45, 93%, 47%, 0.2)'
    },
    advanced: {
      bg: 'hsl(24, 74%, 58%, 0.1)',
      text: 'hsl(24, 74%, 45%)', 
      border: 'hsl(24, 74%, 58%, 0.2)'
    },
    expert: {
      bg: 'hsl(348, 83%, 47%, 0.1)',
      text: 'hsl(348, 83%, 35%)',
      border: 'hsl(348, 83%, 47%, 0.2)'
    }
  };

  const getStockStatus = () => {
    if (!fish.is_available || fish.stock_quantity === 0) {
      return { text: 'Out of Stock', bg: 'hsl(200, 15%, 45%)', color: 'white' };
    } else if (fish.stock_quantity <= 2) {
      return { text: `Low Stock (${fish.stock_quantity})`, bg: 'hsl(45, 93%, 47%)', color: 'white' };
    } else {
      return { text: `In Stock (${fish.stock_quantity})`, bg: 'hsl(142, 76%, 36%)', color: 'white' };
    }
  };

  const stockStatus = getStockStatus();

  const careColors = careLevelColors[fish.care_level] || careLevelColors.beginner;

  return (
    <Link to={`/fish/${fish.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="reef-card" style={{
        overflow: 'hidden',
        transition: 'all 0.5s ease',
        cursor: 'pointer',
        border: '1px solid var(--border)',
        background: 'var(--gradient-card)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-elevated)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-card)';
      }}>
        
        {/* Image Container */}
        <div style={{
          position: 'relative',
          aspectRatio: '4/3',
          overflow: 'hidden'
        }}>
          {fish.image_url && fish.image_url.trim() !== '' && fish.image_url !== 'null' ? (
            <img 
              src={fish.image_url} 
              alt={fish.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.7s ease'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              onError={(e) => {
                // If image fails to load, hide it and show emoji fallback
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div style={{
            width: '100%',
            height: '100%',
            display: fish.image_url && fish.image_url.trim() !== '' && fish.image_url !== 'null' ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--gradient-ocean)',
            fontSize: '3rem'
          }}>
            {fish.image || '🐠'}
          </div>
          
          {/* Hover Overlay */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none'
          }} 
          className="hover-overlay" />
          
          {/* Stock Badge - Top Right */}
          <div style={{
            position: 'absolute',
            top: '0.75rem',
            right: '0.75rem'
          }}>
            <span style={{
              padding: '0.25rem 0.75rem',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              background: stockStatus.bg,
              color: stockStatus.color,
              backdropFilter: 'blur(4px)',
              border: 0,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {stockStatus.text}
            </span>
          </div>

          {/* Category Badge - Top Left */}
          {fish.category && (
            <div style={{
              position: 'absolute',
              top: '0.75rem',
              left: '0.75rem'
            }}>
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: '500',
                background: 'rgba(255, 255, 255, 0.9)',
                color: 'var(--foreground)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.5)'
              }}>
                {fish.category}
              </span>
            </div>
          )}
        </div>

        {/* Card Content */}
        <div style={{
          padding: '1.25rem',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          {/* Header */}
          <div>
            <h3 className="font-display" style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: 'var(--foreground)',
              marginBottom: '0.25rem',
              transition: 'color 0.3s ease'
            }}>
              {fish.name}
            </h3>
            {fish.scientific_name && (
              <p style={{
                fontSize: '0.875rem',
                color: 'var(--muted-foreground)',
                fontStyle: 'italic',
                margin: 0
              }}>
                {fish.scientific_name}
              </p>
            )}
          </div>

          {/* Description */}
          {fish.description && (
            <p style={{
              fontSize: '0.875rem',
              color: 'var(--muted-foreground)',
              lineHeight: '1.5',
              margin: 0,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}>
              {fish.description}
            </p>
          )}

          {/* Badges */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            {fish.care_level && (
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '15px',
                fontSize: '0.75rem',
                fontWeight: '500',
                background: careColors.bg,
                color: careColors.text,
                border: `1px solid ${careColors.border}`,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {fish.care_level}
              </span>
            )}
            {fish.tank_size && (
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '15px',
                fontSize: '0.75rem',
                fontWeight: '500',
                background: 'hsl(190, 70%, 50%, 0.1)',
                color: 'hsl(195, 80%, 35%)',
                border: '1px solid hsl(190, 70%, 50%, 0.2)'
              }}>
                {fish.tank_size}
              </span>
            )}
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: '0.75rem',
            borderTop: '1px solid var(--border)',
            marginTop: 'auto'
          }}>
            {fish.temperament && (
              <span style={{
                fontSize: '0.875rem',
                color: 'var(--muted-foreground)',
                fontWeight: '500'
              }}>
                {fish.temperament}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default EnhancedFishCard;
